/**
 * Daily Stats Service
 * Tracks real-time XP earnings and activity within a session
 * Used by LumoStatsBar to show live progress updates
 * Also triggers strategic upsells on milestones
 */

import { supabase } from '../lib/supabase';

class DailyStatsService {
  constructor() {
    this.sessionXp = 0;
    this.sessionStartTime = Date.now();
    this.listeners = [];
    this.milestoneListeners = []; // For triggering upsells
  }

  /**
   * Subscribe to stats updates
   */
  subscribe(callback) {
    this.listeners.push(callback);
    return () => {
      this.listeners = this.listeners.filter(cb => cb !== callback);
    };
  }

  /**
   * Subscribe to milestone events (for upsells)
   */
  subscribeMilestones(callback) {
    this.milestoneListeners.push(callback);
    return () => {
      this.milestoneListeners = this.milestoneListeners.filter(cb => cb !== callback);
    };
  }

  /**
   * Notify all listeners of stats change
   */
  notify() {
    this.listeners.forEach(cb => cb({
      todayXp: this.sessionXp,
      sessionDuration: Math.floor((Date.now() - this.sessionStartTime) / 1000)
    }));
  }

  /**
   * Add XP to today's total (called after drill completion, roleplay, etc.)
   */
  addXp(amount) {
    const previousXp = this.sessionXp;
    this.sessionXp += amount;
    this.notify();
    
    // Check for milestones and trigger upsells
    this.checkMilestones(previousXp, this.sessionXp);
  }

  /**
   * Check if XP gain crossed a milestone threshold
   */
  checkMilestones(previousXp, newXp) {
    // First drill completion - great upsell moment
    if (previousXp === 0 && newXp > 0) {
      this.triggerMilestone('first_drill_complete', { xpEarned: newXp });
    }
    
    // Hit 100 XP in a session - showing dedication
    if (previousXp < 100 && newXp >= 100) {
      this.triggerMilestone('session_100xp', { totalXp: newXp });
    }
    
    // Hit 250 XP in a session - serious practitioner
    if (previousXp < 250 && newXp >= 250) {
      this.triggerMilestone('session_250xp', { totalXp: newXp });
    }
  }

  /**
   * Notify milestone listeners (triggers upsells)
   */
  triggerMilestone(milestoneType, data) {
    this.milestoneListeners.forEach(cb => cb({
      type: milestoneType,
      data,
      timestamp: Date.now()
    }));
  }

  /**
   * Reset daily stats (called at session start or midnight)
   */
  reset() {
    this.sessionXp = 0;
    this.sessionStartTime = Date.now();
    this.notify();
  }

  /**
   * Get current session stats
   */
  getStats() {
    return {
      todayXp: this.sessionXp,
      sessionDuration: Math.floor((Date.now() - this.sessionStartTime) / 1000)
    };
  }

  /**
   * Load today's XP from database (for accurate count)
   */
  async loadTodayXp() {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return 0;

      const today = new Date().toISOString().split('T')[0];

      // Query all XP-earning activities from today
      // This would require a daily_activity_log table in production
      // For now, we'll use session-based tracking
      
      return this.sessionXp;
    } catch (error) {
      console.error('Error loading today XP:', error);
      return 0;
    }
  }
}

// Singleton instance
export const dailyStatsService = new DailyStatsService();
export default dailyStatsService;
